import { isAxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import { authEvents, getAccessToken } from '@/shared/auth/tokenProvider';
import { HTTP_HEADERS } from '../constants';
import { toApiError } from '../errors';

/** `responseType: 'blob'` turns JSON error bodies into Blobs; read them back so the message survives. */
const readBlobErrorBody = async (response: AxiosResponse | undefined) => {
  if (!response || !(response.data instanceof Blob)) return;
  if (!response.data.type.includes('json')) return;
  try {
    response.data = JSON.parse(await response.data.text()) as unknown;
  } catch {
    // Leave the Blob in place; toApiError falls back to the status text.
  }
};

/**
 * Response error interceptor:
 * 1. 401 → one forced token refresh shared by every request that failed meanwhile, then a single
 *    retry. Still 401 → `session-expired` (the app signs out). No page reloads.
 * 2. Everything else → `ApiError`. It never shows UI or touches the store; that's the caller's job.
 */
export const createErrorInterceptor = (client: AxiosInstance) => {
  let refreshing: Promise<string | null> | null = null;

  const refreshToken = () => {
    refreshing ??= getAccessToken({ forceRefresh: true })
      .catch(() => null)
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  };

  return async (error: unknown): Promise<AxiosResponse> => {
    if (isAxiosError(error) && error.response?.status === 401 && error.config) {
      const { config } = error;
      if (!config.skipAuth && !config._retry) {
        config._retry = true;
        const token = await refreshToken();
        if (token) {
          config.headers.set(HTTP_HEADERS.authorization, `Bearer ${token}`);
          return client.request(config);
        }
      }
      if (!config.skipAuth) authEvents.emit('session-expired');
    }

    if (isAxiosError(error)) await readBlobErrorBody(error.response);
    throw toApiError(error);
  };
};
